/* eslint-disable custom-rules/modal-and-drawer-requires-title */
import { useMediaQuery } from '@uhg-abyss/web/hooks/useMediaQuery';
import { styled } from '@uhg-abyss/web/tools/styled';
import { Button } from '@uhg-abyss/web/ui/Button';
import { Drawer } from '@uhg-abyss/web/ui/Drawer';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { Popover } from '@uhg-abyss/web/ui/Popover';
import { Text } from '@uhg-abyss/web/ui/Text';
import { t } from 'i18next';
import find from 'lodash/find';
import React, { useState } from 'react';

import { ConstantsLagoon } from '../../../../common/ConstantsLagoon';
import { phoneOnly } from '../../../../common/ConstantsStyles';
import { CostIndicatorDrawerStyles } from '../../../../common/DataCard/CostIndicatorPopover.style';
import { useFeatureFlag } from '../../../../hooks/useFeatureFlag';
import { useLagoon } from '../../../../hooks/useLagoon';
import { HealthGradesContainerStyled } from '../PSXHome/HealthGradesContainer.styled';

export const ContentWrapper = styled('div', {
  maxWidth: '1200px',
  marginLeft: 'auto',
  marginRight: 'auto',
  paddingTop: '16px',

  '@media (max-width: 1248px)': {
    marginLeft: '24px',
    marginRight: '0px',
    paddingTop: '24px',
  },
  '@media (max-width: 744px)': {
    marginLeft: '16px',
    marginRight: '0px',
    paddingTop: '16px',
    paddingBottom: '0px',
  },
});

export const ToolTipDisclaimer = () => {
  const mobileScreen = useMediaQuery(phoneOnly);

  const [CostIndicatorDrawerToggle, setCostIndicatorDrawerToggle] =
    useState(false);
  const [distanceShownDrawerToggle, setDistanceShownDrawerToggle] =
    useState(false);
  const lagoonCostIndicatorData = useLagoon('cost-indicator-popover')();
  const costIndicatorData = find(lagoonCostIndicatorData, { key: '1' });
  const costIndicatorContent = costIndicatorData?.costIndicatorPopoverContent;

  const costIndicatorTestId: string = mobileScreen
    ? 'cost-indicator-drawer'
    : 'cost-indicator-popover';

  const distanceIndicatorTestId: string = mobileScreen
    ? 'distance-shown-drawer'
    : 'distance-shown-popover';

  const costIndicatorTitle = costIndicatorData?.title;

  const [enableMapViewHideOption] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_MAP_VIEW_HIDE_OPTION,
  ]);

  const [enableRemoveEstimatedCostText] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_REMOVE_ESTIMATED_COST_TEXT,
  ]);

  return (
    <React.Fragment>
      {enableMapViewHideOption && (
        <HealthGradesContainerStyled>
          <ContentWrapper>
            <Layout.Group
              css={{
                flexDirection: mobileScreen ? 'column' : 'row',
                alignItems: mobileScreen ? 'flex-start' : 'center',
              }}
              space={!mobileScreen && 24}
            >
              {!enableRemoveEstimatedCostText && (
                <React.Fragment>
                  {!mobileScreen ? (
                    <div>
                      <Popover
                        aria-label={'cost-indicator-popover-tooltip'}
                        content={
                          <div
                            dangerouslySetInnerHTML={{
                              __html: costIndicatorContent,
                            }}
                          />
                        }
                        css={{
                          '.abyss-text-root': {
                            lineHeight: '18px',
                            fontSize: '14px',
                            fontWeight: '600',
                          },
                        }}
                        data-auto-testid="about-cost-information"
                        data-testid="about-cost-information"
                        name={'About-cost-information'}
                        onOpenChange={() => {
                          setCostIndicatorDrawerToggle(
                            !CostIndicatorDrawerToggle
                          );
                        }}
                        tabIndex={0}
                        title={costIndicatorTitle}
                        width={220}
                      >
                        <Layout.Group space={4}>
                          <IconSymbol
                            data-auto-testid="cost-indicator-popover-info-icon"
                            data-testid="cost-indicator-popover-info-icon"
                            icon="info"
                            size={mobileScreen ? 16 : 18}
                            variant="outlined"
                          />
                          <Text
                            color="$core.color.brand.60"
                            fontSize="sm"
                            fontWeight="medium"
                          >
                            {t('CONTENT_FILTERS.ABOUT_COST_TOOLTIP')}
                          </Text>
                        </Layout.Group>
                      </Popover>
                    </div>
                  ) : (
                    <React.Fragment>
                      <Drawer
                        css={CostIndicatorDrawerStyles}
                        data-auto-testid={costIndicatorTestId}
                        data-testid={costIndicatorTestId}
                        header={costIndicatorTitle}
                        isOpen={CostIndicatorDrawerToggle}
                        onClose={() => setCostIndicatorDrawerToggle(false)}
                        position="right"
                        size="200"
                      >
                        <div
                          dangerouslySetInnerHTML={{
                            __html: costIndicatorContent,
                          }}
                        />
                      </Drawer>
                      <Layout.Group
                        css={{
                          '.abyss-text-root': {
                            lineHeight: '18px',
                            fontSize: '14px',
                            fontWeight: '600',
                          },
                          '.abyss-button-root': {
                            padding: '0px !important',
                          },
                        }}
                        data-auto-testid={'cost-indicator-drawer-tooltip'}
                        data-testid={'cost-indicator-drawer-tooltip'}
                        name={'About-cost-information'}
                        space={4}
                      >
                        <Button
                          css={{
                            '.abyss-text-root': {
                              paddingLeft: '4px',
                            },
                          }}
                          data-auto-testid="cost-indicator-info-icon"
                          data-testid="cost-indicator-info-icon"
                          onClick={() => setCostIndicatorDrawerToggle(true)}
                          variant="text"
                        >
                          <IconSymbol
                            icon="info"
                            size={mobileScreen ? 16 : 18}
                            variant="outlined"
                          />
                          <Text
                            color="$core.color.brand.60"
                            fontSize="sm"
                            fontWeight="medium"
                          >
                            {t('CONTENT_FILTERS.ABOUT_COST_TOOLTIP')}
                          </Text>
                        </Button>
                      </Layout.Group>
                    </React.Fragment>
                  )}
                </React.Fragment>
              )}
              <React.Fragment>
                {!mobileScreen ? (
                  <div>
                    <Popover
                      aria-label={'distance-shown-popover-tooltip'}
                      content={t('DISTANCE_POPOVER_CONTENT')}
                      css={{
                        '.abyss-text-root': {
                          lineHeight: '18px',
                          fontSize: '14px',
                          fontWeight: '600',
                        },
                      }}
                      data-auto-testid={distanceIndicatorTestId}
                      data-testid={distanceIndicatorTestId}
                      name={'About-distance-information'}
                      onOpenChange={() => {
                        setDistanceShownDrawerToggle(
                          !distanceShownDrawerToggle
                        );
                      }}
                      tabIndex={0}
                      title={t('CONTENT_FILTERS.DISTANCE')}
                      width={220}
                    >
                      <Layout.Group space={4}>
                        <IconSymbol
                          data-auto-testid="cost-indicator-popover-info-icon"
                          data-testid="cost-indicator-popover-info-icon"
                          icon="info"
                          size={mobileScreen ? 16 : 18}
                          variant="outlined"
                        />
                        <Text
                          color="$core.color.brand.60"
                          fontSize="sm"
                          fontWeight="medium"
                        >
                          {t('CONTENT_FILTERS.ABOUT_DISTANCE_TOOLTIP')}
                        </Text>
                      </Layout.Group>
                    </Popover>
                  </div>
                ) : (
                  <React.Fragment>
                    <Drawer
                      css={CostIndicatorDrawerStyles}
                      data-auto-testid={distanceIndicatorTestId}
                      data-testid={distanceIndicatorTestId}
                      header={t('CONTENT_FILTERS.DISTANCE')}
                      isOpen={distanceShownDrawerToggle}
                      onClose={() => setDistanceShownDrawerToggle(false)}
                      position="right"
                      size="200"
                    >
                      {t('DISTANCE_POPOVER_CONTENT')}
                    </Drawer>
                    <Layout.Group
                      css={{
                        '.abyss-text-root': {
                          lineHeight: '18px',
                          fontSize: '14px',
                          fontWeight: '600',
                        },
                        '.abyss-button-root': {
                          padding: '0px !important',
                          height: '24px',
                        },
                      }}
                      data-auto-testid={'distance-shown-drawer-tooltip'}
                      name={'About-distance-shown'}
                      space={4}
                    >
                      <Button
                        css={{
                          '.abyss-text-root': {
                            paddingLeft: '4px',
                          },
                        }}
                        data-auto-testid="distance-shown-info-icon"
                        data-testid="distance-shown-info-icon"
                        onClick={() => setDistanceShownDrawerToggle(true)}
                        variant="text"
                      >
                        <IconSymbol
                          icon="info"
                          size={mobileScreen ? 16 : 18}
                          variant="outlined"
                        />
                        <Text
                          color="$core.color.brand.60"
                          fontSize="sm"
                          fontWeight="medium"
                        >
                          {t('CONTENT_FILTERS.ABOUT_DISTANCE_TOOLTIP')}
                        </Text>
                      </Button>
                    </Layout.Group>
                  </React.Fragment>
                )}
              </React.Fragment>
            </Layout.Group>
          </ContentWrapper>
        </HealthGradesContainerStyled>
      )}
    </React.Fragment>
  );
};
